import { useState, useEffect, useCallback } from 'react'

export interface PreloadOptions {
  delay?: number // 延迟预加载时间（毫秒）
  priority?: 'high' | 'low' // 预加载优先级
  maxConcurrent?: number // 最大并发数
  enableProgress?: boolean // 是否启用进度跟踪
}

export interface PreloadProgress {
  loaded: number
  total: number
  percentage: number
}

export interface UseImagePreloaderReturn {
  preloadedUrls: Set<string>
  progress: PreloadProgress
  isPreloaded: (url: string) => boolean
  preloadSingleImage: (url: string) => Promise<void>
  preloadImages: (urls: string[]) => Promise<void>
}

/**
 * 图片预加载 Hook
 * @param urls 需要预加载的图片 URL 数组
 * @param options 预加载选项
 * @returns 预加载状态和方法
 */
export function useImagePreloader(
  urls: string[] = [],
  options: PreloadOptions = {}
): UseImagePreloaderReturn {
  const {
    delay = 500,
    priority = 'low',
    maxConcurrent = 3,
    enableProgress = true
  } = options

  const [preloadedUrls, setPreloadedUrls] = useState<Set<string>>(new Set())
  const [progress, setProgress] = useState<PreloadProgress>({
    loaded: 0,
    total: 0,
    percentage: 0
  })

  // 预加载单张图片
  const preloadSingleImage = useCallback((url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (preloadedUrls.has(url)) {
        resolve()
        return
      }

      const img = new Image()
      
      // 设置预加载优先级
      if ('fetchPriority' in img) {
        (img as any).fetchPriority = priority
      }
      
      img.onload = () => {
        setPreloadedUrls(prev => new Set(prev).add(url))
        
        if (enableProgress) {
          setProgress(prev => {
            const newLoaded = prev.loaded + 1
            return {
              loaded: newLoaded,
              total: prev.total,
              percentage: prev.total > 0 ? Math.round((newLoaded / prev.total) * 100) : 0
            }
          })
        }
        
        resolve()
      }
      
      img.onerror = () => {
        console.warn(`图片预加载失败: ${url}`)
        
        if (enableProgress) {
          setProgress(prev => {
            const newLoaded = prev.loaded + 1
            return {
              loaded: newLoaded,
              total: prev.total,
              percentage: prev.total > 0 ? Math.round((newLoaded / prev.total) * 100) : 0
            }
          })
        }
        
        reject(new Error(`Failed to preload: ${url}`))
      }
      
      img.src = url
    })
  }, [preloadedUrls, priority, enableProgress])

  // 批量预加载图片
  const preloadImages = useCallback(async (imageUrls: string[]) => {
    if (imageUrls.length === 0) return

    // 初始化进度
    if (enableProgress) {
      setProgress({
        loaded: 0,
        total: imageUrls.length,
        percentage: 0
      })
    }

    // 分批处理，避免同时加载过多图片
    const batches = []
    for (let i = 0; i < imageUrls.length; i += maxConcurrent) {
      batches.push(imageUrls.slice(i, i + maxConcurrent))
    }

    // 逐批预加载
    for (const batch of batches) {
      await Promise.allSettled(batch.map(url => preloadSingleImage(url)))
    }
  }, [preloadSingleImage, maxConcurrent, enableProgress])

  // 检查图片是否已预加载
  const isPreloaded = useCallback((url: string) => {
    return preloadedUrls.has(url)
  }, [preloadedUrls])

  // 自动预加载传入的 URLs
  useEffect(() => {
    if (urls.length === 0) return

    const timer = setTimeout(() => {
      preloadImages(urls)
    }, delay)

    return () => clearTimeout(timer)
  }, [urls, delay, preloadImages])

  return {
    preloadedUrls,
    progress,
    isPreloaded,
    preloadSingleImage,
    preloadImages
  }
}


